import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoJsonLd from '@/components/base/SeoJsonLd';

const allGuides = [
  { title: 'What Is Basetao Spreadsheet?', desc: 'Complete introduction to the ecosystem — what it is, how it works, and why shoppers love it.', icon: 'ri-question-answer-line', link: '/what-is-basetao-spreadsheet', readTime: '8 min' },
  { title: 'How To Use Basetao Spreadsheet', desc: 'Step-by-step walkthrough from navigation basics to advanced filtering techniques.', icon: 'ri-guide-line', link: '/how-to-use-basetao-spreadsheet', readTime: '9 min' },
  { title: 'Basetao Spreadsheet Tutorial', desc: 'Hands-on tutorial covering every aspect with practical examples and real workflows.', icon: 'ri-book-read-line', link: '/basetao-spreadsheet-tutorial', readTime: '10 min' },
  { title: 'Basetao Review 2026', desc: 'Unbiased assessment — features, pricing, shipping, support. Pros, cons, and honest verdict.', icon: 'ri-star-line', link: '/basetao-review-2026', readTime: '8 min' },
  { title: 'Is Basetao Legit?', desc: 'Safety analysis, buyer protections, community verification, and trustworthiness investigation.', icon: 'ri-shield-check-line', link: '/is-basetao-legit', readTime: '7 min' },
  { title: 'Basetao Shipping Cost Guide', desc: 'Full breakdown of shipping methods, costs by weight, and consolidation strategies.', icon: 'ri-ship-line', link: '/basetao-shipping-cost', readTime: '9 min' },
  { title: 'Basetao Coupon & Discount Guide', desc: 'Active codes, seasonal promotions, stacking strategies, and saving tips for 2026.', icon: 'ri-coupon-3-line', link: '/basetao-coupon-guide', readTime: '7 min' },
  { title: 'Basetao Warehouse Guide', desc: 'Storage, consolidation, processing times, and warehouse best practices explained.', icon: 'ri-store-2-line', link: '/basetao-warehouse-guide', readTime: '7 min' },
  { title: 'Basetao QC Guide', desc: 'Master quality control — requesting, reading, and acting on QC photos for every purchase.', icon: 'ri-camera-line', link: '/basetao-qc-guide', readTime: '8 min' },
  { title: 'Basetao Beginner Guide', desc: 'First-timer walkthrough — account setup, first order, terminology, and monthly roadmap.', icon: 'ri-rocket-line', link: '/basetao-beginner-guide', readTime: '8 min' },
];

export default function ShoppingGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const location = useLocation();
  const siteOrigin = import.meta.env.VITE_SITE_URL || 'https://basetaosheet.hair';
  const pageUrl = `${siteOrigin}${location.pathname}`;
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Basetao Shopping Guide',
      description: 'Your complete resource for smarter shopping with the Basetao Spreadsheet. Browse in-depth guides covering product research, quality control, shipping, and savings.',
      url: pageUrl,
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: allGuides.map((guide, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: guide.title,
          url: `${siteOrigin}${guide.link}`,
        })),
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteOrigin}/` },
        { '@type': 'ListItem', position: 2, name: 'Shopping Guide', item: pageUrl },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <SeoJsonLd data={structuredData} />

      <main>
        <header className="w-full pt-24 pb-16 md:pt-32 md:pb-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <div className="flex items-center justify-center gap-2 text-sm text-foreground-500 mb-4">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <span>/</span>
              <span className="text-foreground-700">Shopping Guide</span>
            </div>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold uppercase tracking-wide mb-5">
              Knowledge Hub &bull; 2026
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              Basetao Shopping Guide
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              Your complete resource for smarter shopping with the Basetao Spreadsheet. Browse our collection of in-depth guides covering every aspect of product research, quality control, shipping, and savings.
            </p>
          </div>
        </header>

        <section className="w-full py-16 md:py-20">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {allGuides.map((guide) => (
                <Link
                  key={guide.link}
                  to={guide.link}
                  className="group flex items-start gap-4 p-5 rounded-xl bg-background-100 border border-background-200/70 hover:border-accent-300 hover:bg-accent-50/20 transition-all duration-200"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 group-hover:bg-accent-200 flex-shrink-0 transition-colors">
                    <i className={`${guide.icon} text-xl text-accent-600`}></i>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-accent-700 transition-colors">
                        {guide.title}
                      </h3>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{guide.readTime}</span>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-2">
                      {guide.desc}
                    </p>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-accent-600">
                      Read Now
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-xs"></i>
                      </div>
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-16 p-8 rounded-xl bg-background-100 border border-background-200/70 text-center">
              <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-3">
                Ready to Start Shopping?
              </h2>
              <p className="text-base text-foreground-600 mb-6 max-w-lg mx-auto">
                Put your knowledge into practice. Explore the full Basetao Spreadsheet with all product categories, pricing details, and community reviews.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  Basetao Spreadsheet
                </a>
                <Link
                  to="/"
                  className="px-6 py-3 rounded-lg bg-background-50 border border-background-300 text-foreground-700 text-sm font-semibold hover:bg-background-200/50 transition-colors whitespace-nowrap"
                >
                  Back to Homepage
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}